import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GiftOrder } from '../types';
import {
  chooseSubscriptionGift,
  clearSubscriptionPayloadPreview,
  fetchSubscriptionGifts,
  formatNaira,
} from '../lib/data';
import useSEO from '../hooks/useSEO';
import { Thumb } from '../components/bx';

interface PaymentResponse {
  total_amount_paid: number;
  vendor?: string;
  recipient?: string;
  subscribed_for?: string;
  delivery_address?: string;
  type: 'gift' | 'purchase' | 'self';
}

interface ConfirmationPageProps {
  order: GiftOrder;
  paymentData?: PaymentResponse;
  paymentReference?: string | null;
  onReset: () => void;
}

interface GiftChoice {
  _id: string;
  name: string;
  description?: string;
}

export default function ConfirmationPage({
  order,
  paymentData,
  paymentReference,
  onReset,
}: ConfirmationPageProps) {
  const handleReset = useCallback(() => {
    clearSubscriptionPayloadPreview();
    onReset();
  }, [onReset]);
  const hasHandledBackRef = useRef(false);
  let confirmationType = paymentData?.type;
  if (!confirmationType) {
    if (order.isSubscribe) {
      confirmationType = 'self';
    } else if (order.isGetMe) {
      confirmationType = 'purchase';
    } else {
      confirmationType = 'gift';
    }
  }

  const isSubscription = confirmationType === 'self' || order.isSubscribe;

  useSEO({
    title: isSubscription ? 'Subscription Activated - BOQBOX' : 'Order Confirmed - BOQBOX',
    description: isSubscription
      ? 'Your subscription is active and your free gift is ready to choose.'
      : 'Your gift order has been confirmed and will be delivered soon.',
    keywords: 'order confirmation, delivery confirmation, order status, Lagos',
  });

  const [gifts, setGifts] = useState<GiftChoice[]>([]);
  const [selectedGiftId, setSelectedGiftId] = useState<string | null>(null);
  const [giftsLoading, setGiftsLoading] = useState(false);
  const [giftsError, setGiftsError] = useState<string | null>(null);
  const [giftActionLoading, setGiftActionLoading] = useState(false);
  const [giftActionMessage, setGiftActionMessage] = useState<string | null>(null);
  const [giftActionError, setGiftActionError] = useState<string | null>(null);

  const totalAmount = paymentData?.total_amount_paid || 0;
  const vendorName = paymentData?.vendor || order.vendor?.name || 'N/A';
  const recipientName =
    paymentData?.subscribed_for || paymentData?.recipient || order.recipient.fullName;
  const deliveryAddress =
    paymentData?.delivery_address ||
    [order.recipient.address, order.recipient.city, order.recipient.state]
      .filter(Boolean)
      .join(', ');
  const recurringFrequency =
    order.subscription?.frequency || order.items.find((oi) => oi.subscriptionOption)?.subscriptionOption?.name || 'monthly';
  const preferredDropoffDay = order.subscription?.preferredDropoffDay;
  const deliveryFrequency = order.subscription?.deliveryFrequency;
  const deliveryDays = order.subscription?.deliveryDays;
  const deliveryTimeSlot = order.subscription?.deliveryTimeSlot;
  const businessAddress = order.recipient.businessAddress;
  const sellingSpotName = order.recipient.sellingSpotName;
  const deliveryScheduleCopy = useMemo(() => {
    if (!isSubscription) {
      return '';
    }

    if (order.subscription?.category === 'b2b') {
      const scheduleParts = [
        deliveryFrequency ? `Items delivered ${deliveryFrequency.toLowerCase()}` : `Items delivered ${recurringFrequency}`,
        deliveryDays && deliveryDays.length > 0 ? `on ${deliveryDays.join(', ')}` : '',
        deliveryTimeSlot ? `during the ${deliveryTimeSlot} window` : '',
        sellingSpotName ? `to ${sellingSpotName}` : '',
        businessAddress ? `near ${businessAddress}` : deliveryAddress ? `at ${deliveryAddress}` : '',
      ].filter(Boolean);

      const sundayNote = deliveryDays?.includes('Sunday')
        ? ' Sunday deliveries start at 3:00 PM.'
        : '';

      return `${scheduleParts.join(' ')}.${sundayNote}`.replace(/\s+\./, '.');
    }

    const consumerParts = [
      `Items delivered ${recurringFrequency}`,
      preferredDropoffDay ? `on ${preferredDropoffDay}` : '',
      deliveryAddress ? `to ${deliveryAddress}` : '',
    ].filter(Boolean);

    const sundayNote =
      preferredDropoffDay === 'Sunday' ? ' Sunday deliveries start at 3:00 PM.' : '';

    return `${consumerParts.join(' ')}.${sundayNote}`.replace(/\s+\./, '.');
  }, [
    businessAddress,
    deliveryDays,
    deliveryAddress,
    deliveryFrequency,
    deliveryTimeSlot,
    isSubscription,
    order.subscription?.category,
    preferredDropoffDay,
    recurringFrequency,
    sellingSpotName,
  ]);

  const paymentChoiceLabel = useMemo(() => {
    if (order.subscription?.category !== 'b2b') {
      return '';
    }

    if (order.subscription?.paymentMode === 'monthly') {
      return 'the full month';
    }

    if (order.subscription?.paymentMode === 'daily') {
      return 'today only';
    }

    return '';
  }, [order.subscription?.category, order.subscription?.paymentMode]);

  const paymentConfirmationCopy = useMemo(() => {
    if (!isSubscription) {
      return '';
    }

    const referenceCopy = paymentReference ? ` with reference ${paymentReference}` : '';
    const recipientCopy = recipientName ? ` for ${recipientName}` : '';
    const amountCopy = `Your payment of ${formatNaira(totalAmount)}`;

    if (order.subscription?.category === 'b2b' && paymentChoiceLabel) {
      return `${amountCopy}${recipientCopy} has been confirmed successfully${referenceCopy}, and BOQBOX has recorded this reseller plan as paid for ${paymentChoiceLabel}.`;
    }

    return `${amountCopy}${recipientCopy} has been confirmed successfully${referenceCopy}.`;
  }, [
    isSubscription,
    order.subscription?.category,
    paymentChoiceLabel,
    paymentReference,
    recipientName,
    totalAmount,
  ]);

  const enjoySubscriptionCopy = useMemo(() => {
    if (!isSubscription) {
      return '';
    }

    if (order.subscription?.category === 'b2b') {
      const spotCopy = sellingSpotName ? ` at ${sellingSpotName}` : '';
      return `Sit back and keep selling while ${vendorName} handles discreet restocks${spotCopy} with BOQBOX Queens.`;
    }

    return `Sit back and enjoy stress-free deliveries from ${vendorName}.`;
  }, [isSubscription, order.subscription?.category, sellingSpotName, vendorName]);

  useEffect(() => {
    if (!isSubscription) {
      return;
    }

    hasHandledBackRef.current = false;
    window.history.pushState({ subscriptionConfirmation: true }, '', window.location.href);

    const handlePopState = () => {
      if (hasHandledBackRef.current) {
        return;
      }

      hasHandledBackRef.current = true;
      handleReset();
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [handleReset, isSubscription]);

  useEffect(() => {
    if (!isSubscription) {
      return;
    }

    let active = true;

    const loadGifts = async () => {
      setGiftsLoading(true);
      setGiftsError(null);

      const result = await fetchSubscriptionGifts();

      if (!active) {
        return;
      }

      if (result.success) {
        const availableGifts = (result.data ?? []).filter(
          (gift) => !gift.status || gift.status === 'available'
        );
        setGifts(availableGifts);
        setSelectedGiftId((current) => current ?? availableGifts[0]?._id ?? null);
      } else {
        setGifts([]);
        setGiftsError(result.message || 'Unable to load your free gift options right now.');
      }

      setGiftsLoading(false);
    };

    void loadGifts();

    return () => {
      active = false;
    };
  }, [isSubscription]);


  const selectedGift = useMemo(
    () => gifts.find((gift) => gift._id === selectedGiftId) ?? null,
    [gifts, selectedGiftId]
  );

  const handleSkipGift = () => {
    if (giftActionLoading) {
      return;
    }

    handleReset();
  };

  const handleAddGift = async () => {
    if (!selectedGiftId || giftActionLoading) {
      return;
    }

    if (!paymentReference) {
      setGiftActionError('We could not find your payment reference. Please contact support.');
      return;
    }

    setGiftActionLoading(true);
    setGiftActionError(null);
    setGiftActionMessage(null);

    const result = await chooseSubscriptionGift({
      giftId: selectedGiftId,
      payment_reference: paymentReference,
    });

    if (result.success) {
      setGiftActionMessage(result.message || 'Gift selected successfully.');
      window.setTimeout(() => {
        handleReset();
      }, 500);
      return;
    }

    setGiftActionError(result.message || 'Unable to save your gift choice right now.');
    setGiftActionLoading(false);
  };

  if (!paymentData) {
    return (
      <div className="bx-container">
        <div className="bx-card bx-status">
          <div className="bx-status-icon pending" aria-hidden="true"><span className="bx-spinner" style={{ width: 30, height: 30, borderWidth: 3 }} /></div>
          <h1>Loading your confirmation…</h1>
        </div>
      </div>
    );
  }

  if (isSubscription) {
    return (
      <div className="app-frame page-enter">
        <div className="subscription-success-page">
          <section className="subscription-success-hero">
            <div className="subscription-success-icon">
              <svg
                width="40"
                height="40"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
              >
                <path d="M20 6 9 17l-5-5" />
              </svg>
            </div>
            <h1>Subscription Activated!</h1>
            <p>
              Your {recurringFrequency} subscription is active. We&apos;ll deliver your items on
              schedule, hassle-free.
            </p>
          </section>

          <section className="subscription-success-card">
            <h2>What Happens Next</h2>
            <div className="subscription-success-step">
              <span>1</span>
              <div>
                <strong>Payment Confirmation</strong>
                <p>{paymentConfirmationCopy}</p>
              </div>
            </div>
            <div className="subscription-success-step">
              <span>2</span>
              <div>
                <strong>Delivery Schedule</strong>
                <p>{deliveryScheduleCopy}</p>
              </div>
            </div>
            <div className="subscription-success-step">
              <span>3</span>
              <div>
                <strong>Enjoy Your Subscription</strong>
                <p>{enjoySubscriptionCopy}</p>
              </div>
            </div>
          </section>


          <section className="subscription-gift-card">
            <div className="subscription-gift-header">
              <div className="subscription-gift-badge">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M20 12v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-8" />
                  <path d="M2 7h20v5H2z" />
                  <path d="M12 22V7" />
                  <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7Z" />
                  <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7Z" />
                </svg>
              </div>
              <div>
                <h2>Choose Your Free Gift</h2>
                <p>Thank you for subscribing! Pick one:</p>
              </div>
            </div>

            {giftsLoading ? (
              <p className="subscription-gift-loading">Loading your gift options...</p>
            ) : null}
            {giftsError ? <p className="subscription-gift-error">{giftsError}</p> : null}
            {giftActionMessage ? (
              <p className="subscription-gift-success">{giftActionMessage}</p>
            ) : null}
            {giftActionError ? <p className="subscription-gift-error">{giftActionError}</p> : null}

            {!giftsLoading && !giftsError ? (
              <div className="subscription-gift-options">
                {gifts.map((gift) => {
                  const isSelected = gift._id === selectedGiftId;

                  return (
                    <button
                      key={gift._id}
                      type="button"
                      className={`subscription-gift-option ${isSelected ? 'selected' : ''}`}
                      onClick={() => setSelectedGiftId(gift._id)}
                    >
                      <div>
                        <strong>{gift.name}</strong>
                        <p>{gift.description || 'Complimentary gift with your subscription.'}</p>
                      </div>
                      {isSelected ? (
                        <span className="subscription-gift-check">
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.2"
                          >
                            <path d="M20 6 9 17l-5-5" />
                          </svg>
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            ) : null}

            <div className="subscription-gift-actions">
              <button
                type="button"
                className="subscription-gift-skip"
                onClick={handleSkipGift}
                disabled={giftActionLoading}
              >
                Skip Gift
              </button>
              <button
                type="button"
                className="subscription-gift-add"
                onClick={() => {
                  void handleAddGift();
                }}
                disabled={!selectedGift || giftActionLoading || !paymentReference}
              >
                {giftActionLoading ? 'Adding Gift...' : 'Add Gift'}
              </button>
            </div>
          </section>

          <button type="button" className="subscription-success-home" onClick={handleReset}>
            Back to Home
          </button>

          <div className="subscription-success-support">
            <p>Need help or have questions?</p>
            <a href="tel:08075977046">Call us: 08075977046</a>
          </div>
        </div>
      </div>
    );
  }

  const isPurchase = confirmationType === 'purchase';
  return (
    <div className="bx-container" style={{ paddingTop: 8, paddingBottom: 32 }}>
      <div className="bx-card bx-status">
        <div className="bx-status-icon" aria-hidden="true">✓</div>
        <h1>{isPurchase ? 'Your order is on its way' : `Your gift is on its way to ${recipientName}`}</h1>
        <p>
          {isPurchase
            ? 'Payment received. We’re preparing your order and will deliver it in a plain, unlabelled bag.'
            : `Payment received. We’ll deliver it discreetly and let ${recipientName} know it’s from you.`}
        </p>
        <div style={{ width: '100%', textAlign: 'left', marginTop: 6 }}>
          {order.items.map((oi) => (
            <div key={oi.item._id || oi.item.id} className="bx-cart-line">
              <Thumb src={oi.item.image_url} category={oi.item.category} />
              <div style={{ minWidth: 0 }}>
                <strong>{oi.item.name}</strong>
                <small>Qty {oi.quantity}</small>
              </div>
              <strong>{formatNaira((oi.item.price || 0) * oi.quantity)}</strong>
            </div>
          ))}
          <div className="bx-sum" style={{ marginTop: 14 }}>
            <div className="bx-sum-row"><span>Vendor</span><strong>{vendorName}</strong></div>
            <div className="bx-sum-row"><span>{isPurchase ? 'Deliver to' : 'Recipient'}</span><strong>{recipientName}</strong></div>
            <div className="bx-sum-row"><span>Address</span><strong>{deliveryAddress}</strong></div>
            {paymentReference ? <div className="bx-sum-row"><span>Reference</span><strong style={{ fontFamily: 'ui-monospace, monospace', fontSize: 13 }}>{paymentReference}</strong></div> : null}
            <div className="bx-sum-total"><span>Total paid</span><span>{formatNaira(totalAmount)}</span></div>
          </div>
        </div>
        <p style={{ fontSize: 13.5 }}>A receipt is on its way to {order.sender.email || 'your email'}.</p>
        <button type="button" className="bx-btn bx-btn-primary bx-btn-lg bx-btn-block" onClick={onReset}>
          {isPurchase ? 'Order something else' : 'Send another gift'}
        </button>
      </div>
    </div>
  );
}
